<admin-app-header></admin-app-header>

<div class="container-fluid">
    <div class="row header">
        <div class="col-12">
            <h2 class="mb-3">Database Migrations</h2>
            <p class="text-muted">One-time data migrations and bulk update functions. Migration status is tracked to prevent accidental re-runs.</p>
        </div>
    </div>

    <div *ngIf="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading migration statuses...</p>
    </div>

    <div *ngIf="!loading">
        <!-- Parker Migration Status Dashboard -->
        <div class="row mb-4">
            <div class="col-12">
                <div class="card border-primary">
                    <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                        <h5 class="mb-0"><i class="fas fa-exchange-alt me-2"></i>Parker Migration Status</h5>
                        <div>
                            <button class="btn btn-sm btn-light me-2" (click)="checkParkerMigrationStatus()" [disabled]="parkerMigrationStatusLoading">
                                <span *ngIf="parkerMigrationStatusLoading" class="spinner-border spinner-border-sm me-1"></span>
                                <i *ngIf="!parkerMigrationStatusLoading" class="fas fa-sync-alt me-1"></i>
                                {{ parkerMigrationStatusLoading ? 'Scanning...' : 'Check Status' }}
                            </button>
                            <button class="btn btn-sm btn-warning" (click)="runParkerMigrationWithResume()">
                                <i class="fas fa-play me-1"></i> Run / Resume Migration
                            </button>
                        </div>
                    </div>
                    <div class="card-body">
                        <div *ngIf="!parkerMigrationStatus && !parkerMigrationStatusLoading && !parkerMigrationStatusError" class="text-muted">
                            Click "Check Status" to scan the database and see migration progress. This may take a minute.
                        </div>

                        <div *ngIf="parkerMigrationStatusLoading" class="text-center py-3">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p class="mt-2 text-muted">Scanning all clients... this may take a few minutes.</p>
                        </div>

                        <div *ngIf="parkerMigrationStatusError" class="alert alert-danger mb-0">
                            <i class="fas fa-exclamation-triangle me-2"></i>{{ parkerMigrationStatusError }}
                        </div>

                        <div *ngIf="parkerMigrationStatus && !parkerMigrationStatusLoading">
                            <div *ngIf="parkerMigrationStatus.migrationComplete" class="alert alert-success mb-3">
                                <i class="fas fa-check-circle me-2"></i><strong>Migration complete!</strong> All clients that need parkers have been migrated.
                            </div>
                            <div *ngIf="!parkerMigrationStatus.migrationComplete" class="alert alert-warning mb-3">
                                <i class="fas fa-exclamation-circle me-2"></i><strong>Migration incomplete.</strong> {{ parkerMigrationStatus.clientsNeedingMigration }} client(s) still need to be migrated.
                            </div>

                            <div class="row text-center">
                                <div class="col">
                                    <div class="border rounded p-3">
                                        <div class="fs-3 fw-bold">{{ parkerMigrationStatus.totalClients }}</div>
                                        <div class="text-muted small">Total Clients</div>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="border rounded p-3">
                                        <div class="fs-3 fw-bold">{{ parkerMigrationStatus.clientsWithOccupants }}</div>
                                        <div class="text-muted small">Clients with Occupants</div>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="border rounded p-3">
                                        <div class="fs-3 fw-bold text-success">{{ parkerMigrationStatus.clientsWithDefaultParker }}</div>
                                        <div class="text-muted small">Clients with Default Parker</div>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="border rounded p-3">
                                        <div class="fs-3 fw-bold" [class.text-danger]="parkerMigrationStatus.clientsNeedingMigration > 0" [class.text-success]="parkerMigrationStatus.clientsNeedingMigration === 0">
                                            {{ parkerMigrationStatus.clientsNeedingMigration }}
                                        </div>
                                        <div class="text-muted small">Still Need Migration</div>
                                    </div>
                                </div>
                            </div>
                            <div class="row text-center mt-3">
                                <div class="col">
                                    <div class="border rounded p-3">
                                        <div class="fs-3 fw-bold">{{ parkerMigrationStatus.totalOccupants }}</div>
                                        <div class="text-muted small">Total Occupants (old)</div>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="border rounded p-3">
                                        <div class="fs-3 fw-bold text-success">{{ parkerMigrationStatus.totalVehiclesMigrated }}</div>
                                        <div class="text-muted small">Vehicles Migrated (new)</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Legend -->
        <div class="row mb-4">
            <div class="col-12">
                <div class="card">
                    <div class="card-body py-2">
                        <span class="me-4"><strong>Risk Levels:</strong></span>
                        <span class="badge bg-success me-2">Safe</span> Can be run multiple times without side effects
                        <span class="badge bg-warning text-dark ms-3 me-2">Warning</span> Should typically only be run once
                        <span class="badge bg-danger ms-3 me-2">Danger</span> Irreversible changes - use with caution
                    </div>
                </div>
            </div>
        </div>

        <!-- Migration Categories -->
        <div *ngFor="let category of categories" class="row mb-4">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0"><i class="fas fa-database me-2"></i>{{ category }}</h5>
                    </div>
                    <div class="card-body p-0">
                        <table class="table table-hover mb-0">
                            <thead class="table-light">
                                <tr>
                                    <th style="width: 25%">Migration</th>
                                    <th style="width: 35%">Description</th>
                                    <th style="width: 10%" class="text-center">Risk</th>
                                    <th style="width: 10%" class="text-center">Run Count</th>
                                    <th style="width: 12%">Last Run</th>
                                    <th style="width: 8%" class="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let migration of getMigrationsByCategory(category)">
                                    <td>
                                        <strong>{{ migration.name }}</strong>
                                        <br />
                                        <code class="small text-muted">{{ migration.functionName }}</code>
                                    </td>
                                    <td class="small">{{ migration.description }}</td>
                                    <td class="text-center">
                                        <span [class]="getDangerLevelClass(migration.dangerLevel)">
                                            {{ migration.dangerLevel | titlecase }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <span *ngIf="migration.runCount" class="badge bg-secondary">{{ migration.runCount }}</span>
                                        <span *ngIf="!migration.runCount" class="text-muted">-</span>
                                    </td>
                                    <td class="small">
                                        <span *ngIf="migration.lastRunAt">
                                            {{ formatDate(migration.lastRunAt) }}
                                        </span>
                                        <span *ngIf="!migration.lastRunAt" class="text-muted">Never</span>
                                    </td>
                                    <td class="text-center">
                                        <button [class]="getButtonClass(migration.dangerLevel)" [disabled]="migration.isRunning" (click)="runMigration(migration)">
                                            <span *ngIf="migration.isRunning" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                            <i *ngIf="!migration.isRunning" class="fas fa-play"></i>
                                            {{ migration.isRunning ? 'Running...' : 'Run' }}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Info Card -->
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0"><i class="fas fa-info-circle me-2"></i>Information</h5>
                    </div>
                    <div class="card-body">
                        <div class="alert alert-info mb-0">
                            <i class="fas fa-lightbulb me-2"></i>
                            <strong>Migration Tracking:</strong> Each migration run is logged with timestamp and result. Even "safe" migrations are tracked for audit purposes. Check the Firebase console's <code>migrations</code> collection for detailed run history.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
